import { ref, Ref } from 'vue';
import { useTask } from '@/ui/concerns';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { filter as getAllGroups } from '@/services/api/resources/group';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllCategories } from '@/services/api/resources/category';
import { filter as getAllChecklists } from '@/services/api/resources/checklist';
import {
  filterFormItemToOptions,
  filterListToOptions,
  filterSimpleListToOptions,
  sequence,
} from '@/ui/helpers/uiHelper';
import { filter as getAllEntityFoldersWithItems } from '@/services/api/resources/entityFolder';
import { publicFilters as getAllPublicFilters } from '@/services/api/resources/analytics-builder/actions';
import { SelectOptions, SelectOptionsWithTooltip } from '@/ui/types';
import * as TE from 'fp-ts/TaskEither';
import {
  analyticsFilter as getSections,
  publicAnalyticsFilter as getPublicSections,
} from '@/services/api/resources/section';
import { analyticsFilterList as getFormItems } from '@/services/api/resources/question';
import { retrieveApprovalStatuses, retrieveFormItem } from '@/services/analytics/analyticsService';
import {
  FilterType,
  FormStatusType,
  MetricsType,
  optionalFilters,
  PriorityType,
  PunctualityType,
} from '@/services/api/resources/analytics-builder/constants';
import { t } from '@/ui/i18n/i18n';
import { IssueStatus } from '@/entities/todo';
import { TaskEither } from 'fp-ts/lib/TaskEither';
import { ApiError } from '@/services/api';
import { FilterList } from '@/entities/filter/model';
import { useFormItemsStore } from '@/stores/analytics/formItems';
import { ReportFilter, ReportFilterValues } from '@/services/api/resources/analytics-builder/model';
import { v4 as uuidv4 } from 'uuid';

const dummyOptionalTask = () => TE.right({ data: [] });

export interface FilterConfiguration {
  metric: MetricsType | null;
  enabledOptionalFilters: Array<FilterType>;
}

export function useFilters(selectedChecklists: Ref<number[]>) {
  const store = useFormItemsStore();
  const shouldFetchCommonFilters = ref(true);

  const entityItems = ref([] as SelectOptions);
  const groups = ref([] as SelectOptions);
  const users = ref([] as SelectOptions);
  const categories = ref([] as SelectOptions);
  const checklists = ref([] as SelectOptions);
  const entityFolders = ref([] as SelectOptions);
  // Optionals
  const approvalStatuses = ref([] as SelectOptions);
  const sections = ref([] as SelectOptions<string>);
  const formItems = ref([] as SelectOptionsWithTooltip<string | null>);
  // Fixed
  const punctuality: SelectOptions<number | null> = [
    { value: null, label: t('common.all') },
    { value: PunctualityType.inTime, label: t('analytics.in-time') },
    { value: PunctualityType.tooLate, label: t('analytics.too-late') },
  ];
  const priority: SelectOptions<number | null> = [
    { value: null, label: t('common.all') },
    { value: PriorityType.normal, label: t('common.normal') },
    { value: PriorityType.high, label: t('common.high') },
  ];
  const formStatus: SelectOptions<number | null> = [
    { value: FormStatusType.Due, label: t('common.due') },
    { value: FormStatusType.Overdue, label: t('common.overdue') },
  ];
  const actionStatus: SelectOptions<number | null> = [
    { value: IssueStatus.Pending, label: t('status.pending') },
    { value: IssueStatus.Done, label: t('status.done') },
    { value: IssueStatus.InProgress, label: t('status.in-progress') },
    { value: IssueStatus.CantDo, label: t('status.cant-do') },
  ];

  const getCommonFilter = (getFn: () => TaskEither<ApiError, FilterList>) =>
    shouldFetchCommonFilters.value ? getFn() : dummyOptionalTask();

  const makeFormItemTooltip = (checklistTitle: string) => `${t('common.template')}: ${checklistTitle}`;

  const getFilters = useTask(
    () => sequence(
      getCommonFilter(getAllGroups),
      getCommonFilter(getAllEntityItems),
      getCommonFilter(getAllUsers),
      getCommonFilter(getAllCategories),
      getCommonFilter(getAllChecklists),
      getCommonFilter(getAllEntityFoldersWithItems),
      getCommonFilter(retrieveApprovalStatuses),
    ),
    ([
      groupList,
      entityItemsList,
      userList,
      categoryList,
      checklistList,
      entityFoldersList,
      approvalStatusesList,
    ]) => {
      if (shouldFetchCommonFilters.value) {
        entityItems.value = filterListToOptions(entityItemsList);
        groups.value = filterListToOptions(groupList);
        users.value = filterListToOptions(userList);
        categories.value = filterListToOptions(categoryList);
        checklists.value = filterListToOptions(checklistList, t('checklist.no-title2'));
        entityFolders.value = filterListToOptions(entityFoldersList);
        approvalStatuses.value = filterListToOptions(approvalStatusesList);
        shouldFetchCommonFilters.value = false;
      }
    },
  );

  const getPublicFilters = (reportId: number) => useTask(
    () => getAllPublicFilters(reportId),
    publicFilters => {
      users.value = filterSimpleListToOptions(publicFilters.data.users);
      groups.value = filterSimpleListToOptions(publicFilters.data.groups);
      checklists.value = filterSimpleListToOptions(publicFilters.data.checklists, t('checklist.no-title2'));
      categories.value = filterSimpleListToOptions(publicFilters.data.categories);
      entityItems.value = filterSimpleListToOptions(publicFilters.data.entityItems);
      entityFolders.value = filterSimpleListToOptions(publicFilters.data.entityFolders);
      approvalStatuses.value = filterSimpleListToOptions(publicFilters.data.approvalStatuses);
    },
    uuidv4(),
    e => console.error(e),
  );

  const getSectionFilter = useTask(
    () => getSections({ templates: selectedChecklists.value }),
    sectionList => {
      sections.value = filterListToOptions(sectionList);
    },
  );

  const getPublicSectionFilter = (reportId: number) => useTask(
    () => getPublicSections(reportId, { templates: selectedChecklists.value }),
    sectionList => {
      sections.value = filterListToOptions(sectionList);
    },
  );

  const getFormItemFilter = useTask(
    () => getFormItems({ templates: selectedChecklists.value }),
    formItemList => {
      formItems.value = filterFormItemToOptions(formItemList, makeFormItemTooltip);
      store.addAll(formItemList);
    },
  );

  const getPublicFormItemFilter = (reportId: number, formItemId: string | null | undefined) => formItemId
    ? useTask(
      () => retrieveFormItem(formItemId, reportId),
      formItemList => {
        formItems.value = filterFormItemToOptions(formItemList, makeFormItemTooltip);
        store.addAll(formItemList);
      },
    )
    : useTask(
      () => TE.right(true),
      () => {
        formItems.value = [];
      },
    );

  return {
    getFilters,
    getPublicFilters,
    getSectionFilter,
    getPublicSectionFilter,
    getFormItemFilter,
    getPublicFormItemFilter,
    shouldFetchCommonFilters,
    entities: entityItems,
    folders: entityFolders,
    approvalStatuses,
    groups,
    users,
    categories,
    checklists,
    sections,
    formItems,
    punctuality,
    priority,
    formStatus,
    actionStatus,
  };
}

export function useFilterConfiguration() {
  const filtersConfig: Array<FilterConfiguration> = [
    {
      metric: 'submitted-forms',
      enabledOptionalFilters: [FilterType.approvalStatus, FilterType.approvers, FilterType.approvalDate],
    },
    {
      metric: 'form-score',
      enabledOptionalFilters: [FilterType.section, FilterType.formItem, FilterType.approvalStatus,
        FilterType.approvers, FilterType.approvalDate],
    },
    {
      metric: 'form-items',
      enabledOptionalFilters: [FilterType.formItem, FilterType.approvalStatus, FilterType.approvers,
        FilterType.approvalDate],
    },
    {
      metric: 'negative-responses',
      enabledOptionalFilters: [FilterType.section, FilterType.formItem, FilterType.approvalStatus,
        FilterType.approvers, FilterType.approvalDate],
    },
    {
      metric: 'form-item-score',
      enabledOptionalFilters: [FilterType.approvalStatus, FilterType.approvers, FilterType.approvalDate,
        FilterType.section, FilterType.formItem],
    },
    {
      metric: 'schedule-forms',
      enabledOptionalFilters: [FilterType.formStatus],
    },
    {
      metric: 'actions',
      enabledOptionalFilters: [FilterType.actionStatus, FilterType.creator, FilterType.priority,
        FilterType.section, FilterType.formItem, FilterType.approvalStatus, FilterType.approvers, FilterType.approvalDate],
    },
  ];

  const isFilterEnabled = (metric: MetricsType | null | undefined, filter: FilterType): boolean => {
    const filterConfig = filtersConfig.find(config => config.metric === metric);

    if (!filterConfig) {
      return false;
    }

    return filterConfig.enabledOptionalFilters.includes(filter);
  };

  const getDisabledOptionalFilters = (metric: MetricsType | null | undefined): Array<FilterType> => {
    const filterConfig = filtersConfig.find(config => config.metric === metric);

    if (!filterConfig) {
      return optionalFilters;
    }

    return optionalFilters.filter(filter => !filterConfig.enabledOptionalFilters.includes(filter));
  };

  const containsDisabledFilters = (filters: ReportFilter, disabled: Array<FilterType>): boolean =>
    Object.keys(filters).some(key => disabled.includes(key as FilterType));

  const getOnlyActiveFilters = (filters: ReportFilter, disabled: Array<FilterType>): ReportFilter =>
    Object.fromEntries(Object.entries(filters).filter(([key]) => !disabled.includes(key as FilterType))) as ReportFilter;

  const countFilters = (filters: ReportFilter) => {
    let count = 0;
    const singleFilters = [FilterType.punctuality, FilterType.formItem, FilterType.response,
      FilterType.priority, FilterType.date, FilterType.approvalDate];

    Object.values(filters).forEach(filter => {
      if (filter !== null && (filter as ReportFilterValues).values?.length > 0) {
        count++;
      }
    });

    singleFilters.forEach(filter => {
      if (filter === FilterType.date) {
        if (filters.date.range != null) {
          count++;
        }
        return;
      }

      if (filter === FilterType.approvalDate) {
        if (filters.approvalDate?.range != null) {
          count++;
        }
        return;
      }

      if (filters[filter as FilterType] !== undefined && filters[filter] !== null) {
        count++;
      }
    });

    return count;
  };

  return {
    filtersConfig,
    isFilterEnabled,
    getDisabledOptionalFilters,
    containsDisabledFilters,
    getOnlyActiveFilters,
    countFilters,
  };
}
